<template>
  <transition name="error-fade">
    <div v-if="errorStore.isVisible" class="error-screen-overlay">
      <div class="error-screen">
        <div class="error-icon">
          <div class="error-symbol">!</div>
        </div>

        <div class="error-content">
          <h2 class="error-title">{{ errorStore.currentError?.title }}</h2>
          <p class="error-message">{{ errorStore.currentError?.message }}</p>
        </div>

        <div class="error-actions">
          <button class="error-button primary" @click="handleAction">
            {{ errorStore.currentError?.actionText }}
          </button>

          <button
            v-if="showSecondaryAction"
            class="error-button secondary"
            @click="errorStore.hideError()"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useErrorStore } from '@/stores/errorStore';
import { useGameAudioStore } from '@/stores/gameAudioStore';

const errorStore = useErrorStore();
const gameAudio = useGameAudioStore();

const SYSTEM_AUDIO = '/assets/games/audio';

// Map error types to voice clips
const errorVoiceMap: Record<string, { title: string; message: string }> = {
  network: {
    title: `${SYSTEM_AUDIO}/system-connection-problem.mp3`,
    message: `${SYSTEM_AUDIO}/system-we-re-having-trouble-con.mp3`,
  },
  settings: {
    title: `${SYSTEM_AUDIO}/system-game-loading-error.mp3`,
    message: `${SYSTEM_AUDIO}/system-we-couldn-t-load-the-gam.mp3`,
  },
  general: {
    title: `${SYSTEM_AUDIO}/system-oops.mp3`,
    message: `${SYSTEM_AUDIO}/system-something-unexpected-hap.mp3`,
  },
};

// Play voice when error screen appears
watch(
  () => errorStore.isVisible,
  (visible) => {
    if (visible && errorStore.currentError) {
      const clips = errorVoiceMap[errorStore.currentError.type];
      if (clips) {
        // Play the message body (more informative than just the title)
        gameAudio.playSpeech(clips.message);
      }
    }
  }
);

const showSecondaryAction = computed(() => {
  return errorStore.currentError?.showRetry === true;
});

function handleAction() {
  if (errorStore.currentError?.action) {
    errorStore.currentError.action();
  }
  errorStore.hideError();
}
</script>

<style scoped>
.error-screen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.error-screen {
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.3);
  max-width: 28rem;
  width: 100%;
  padding: 2rem;
  text-align: center;
  animation: error-bounce 0.5s ease-out;
}

.error-icon {
  margin-bottom: 1.5rem;
}

.error-symbol {
  width: 4rem;
  height: 4rem;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 auto;
}

.error-content {
  margin-bottom: 2rem;
}

.error-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
  margin: 0 0 1rem 0;
}

.error-message {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

.error-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
}

.error-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 8rem;
}

.error-button.primary {
  background: #3b82f6;
  color: white;
}

.error-button.primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.error-button.secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.error-button.secondary:hover {
  background: #e5e7eb;
  color: #374151;
}

.error-button:active {
  transform: translateY(0);
}

/* Transitions */
.error-fade-enter-active,
.error-fade-leave-active {
  transition: opacity 0.3s ease;
}

.error-fade-enter-from,
.error-fade-leave-to {
  opacity: 0;
}

/* Animations */
@keyframes error-bounce {
  0% {
    transform: scale(0.8) translateY(-20px);
    opacity: 0;
  }
  50% {
    transform: scale(1.05) translateY(-5px);
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 480px) {
  .error-screen {
    padding: 1.5rem;
    margin: 1rem;
  }

  .error-title {
    font-size: 1.25rem;
  }

  .error-message {
    font-size: 0.875rem;
  }

  .error-actions {
    flex-direction: column;
  }

  .error-button {
    width: 100%;
  }
}
</style>
